import React, { useEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@chatsdet/ui';
import type { BoardCardRenderState } from './types';

interface BoardCardProps {
  id: string;
  data?: Record<string, unknown>;
  disabled?: boolean;
  isOverlay?: boolean;
  onClick?: () => void;
  className?: string;
  children: React.ReactNode | ((state: BoardCardRenderState) => React.ReactNode);
}

export function BoardCard({
  id,
  data,
  disabled,
  isOverlay,
  onClick,
  className,
  children,
}: BoardCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: disabled || isOverlay, data });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const state: BoardCardRenderState = { isDragging, isOverlay: !!isOverlay };
  const content = typeof children === 'function' ? children(state) : children;

  // Suppress the click that fires on pointerup after a real drag — dnd-kit
  // clears `isDragging` before the click event, so checking it inside onClick
  // isn't enough on its own.
  const wasDraggingRef = useRef(false);
  useEffect(() => {
    if (isDragging) wasDraggingRef.current = true;
  }, [isDragging]);

  return (
    <div
      ref={isOverlay ? undefined : setNodeRef}
      style={isOverlay ? undefined : style}
      className={cn(
        'select-none',
        isDragging && !isOverlay && 'opacity-30',
        isOverlay && 'shadow-md rotate-[1deg] scale-[1.02]',
        !disabled && !isDragging && 'cursor-grab active:cursor-grabbing',
        disabled && 'cursor-pointer',
        className,
      )}
      onClick={(e) => {
        if (wasDraggingRef.current) {
          wasDraggingRef.current = false;
          return;
        }
        if (!isDragging) {
          e.stopPropagation();
          onClick?.();
        }
      }}
      {...(!disabled && !isOverlay ? { ...attributes, ...listeners } : {})}
    >
      {content}
    </div>
  );
}
